import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Printer, CheckCircle, Warehouse, Send, Edit, FileText, Receipt } from 'lucide-react';
import { EmailStatus } from '../components/EmailStatus';
import RecordPaymentDialog from '../components/RecordPaymentDialog';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/apiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const companyInfo = {
  name: "NJK Logistics Pte Ltd",
  address: "212A Punggol Walk #12-741 Singapore 821212",
  phone: "98366186",
  email: "inquiry@njklogistics.com",
  uen: "202606027K",
  paynow_uen: "202606027K"
};

export const WarehouseInvoiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const printRef = useRef();

  const fetchInvoice = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/warehouse/invoices/${id}`);
      setInvoice(res.data);
    } catch (error) {
      toast.error('Failed to load invoice');
      navigate('/admin/warehouse');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  const handleMarkPaid = async () => {
    try {
      await axios.patch(`${BACKEND_URL}/api/warehouse/invoices/${id}/status`, { status: 'paid' });
      toast.success('Invoice marked as paid');
      fetchInvoice();
    } catch (error) {
      toast.error('Failed to update invoice');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleSendEmail = async () => {
    if (!invoice.customer_email) {
      toast.error('No customer email. Please check the quotation has an email address.');
      return;
    }
    try {
      const res = await axios.post(`${BACKEND_URL}/api/warehouse/invoices/${id}/send-email`);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send email'));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-SG', {
      style: 'currency',
      currency: 'SGD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-SG', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-44 px-4 flex justify-center items-center">
        <p>Loading...</p>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="min-h-screen pt-44 px-4 flex justify-center items-center">
        <p>Invoice not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-44 pb-12 px-4 bg-gray-50">
      <div className="container mx-auto max-w-4xl">
        {/* Action Bar */}
        <div className="flex items-center justify-between gap-4 mb-8 print:hidden">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/admin/warehouse')}>
              <ArrowLeft size={16} className="mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Warehouse className="text-green-600" />
                {invoice.invoice_number}
              </h1>
              <p className="text-gray-600">
                Warehouse Invoice
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(`/admin/warehouse/invoice/${id}/edit`)} data-testid="edit-warehouse-invoice-btn">
              <Edit size={16} className="mr-2" />
              Edit
            </Button>
            {invoice.status === 'unpaid' && (
              <Button onClick={handleMarkPaid}>
                <CheckCircle size={16} className="mr-2" />
                Mark as Paid
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowPaymentModal(true)}
                    className="text-emerald-700 border-emerald-600 hover:bg-emerald-50"
                    data-testid="record-payment-btn">
              <Receipt size={16} className="mr-2" /> Record Payment
            </Button>
            <Button variant="outline" onClick={() => window.open(`${BACKEND_URL}/api/warehouse/invoices/${id}/pdf`, '_blank')} data-testid="preview-pdf-btn">
              <FileText size={16} className="mr-2" />
              Preview PDF
            </Button>
            <Button variant="outline" onClick={handlePrint}>
              <Printer size={16} className="mr-2" />
              Print
            </Button>
            <Button onClick={handleSendEmail} className="bg-green-600 hover:bg-green-700">
              <Send size={16} className="mr-2" />
              Send to Customer
            </Button>
          </div>
          <EmailStatus docType="warehouse_invoice" docId={id} />
        </div>

        {/* Invoice Document */}
        <div ref={printRef} className="bg-white rounded-lg shadow-lg p-8 print:shadow-none print:p-4">
          {/* Header */}
          <div className="border-b-2 border-green-600 pb-6 mb-6">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-2xl font-bold text-green-600">{companyInfo.name}</h1>
                <p className="text-gray-600">{companyInfo.address}</p>
                <p className="text-gray-600">Tel: {companyInfo.phone}</p>
                <p className="text-gray-600">UEN: {companyInfo.uen}</p>
              </div>
              <div className="text-right">
                <h2 className="text-xl font-bold">WAREHOUSE INVOICE</h2>
                <p className="text-lg font-semibold text-green-600">{invoice.invoice_number}</p>
                <p className="text-gray-600">Invoice Date: <span className="font-semibold">{formatDate(invoice.invoice_date)}</span></p>
                <p className="text-gray-600">Payment Terms: <span className="font-semibold">{invoice.payment_terms || 'COD - Cash on Delivery'}</span></p>
                <p className={`mt-2 inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                  invoice.status === 'paid' ? 'bg-green-100 text-green-800' : 
                  invoice.status === 'overdue' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {invoice.status?.toUpperCase()}
                </p>
              </div>
            </div>
          </div>

          {/* Customer Details */}
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <h3 className="font-semibold text-gray-800 mb-2">Bill To:</h3>
            <p className="font-semibold">{invoice.company_name || invoice.customer_name}</p>
            {invoice.company_name && <p>{invoice.customer_name}</p>}
            {invoice.customer_address && <p>{invoice.customer_address}</p>}
            {invoice.customer_phone && <p>Tel: {invoice.customer_phone}</p>}
            {invoice.customer_email && <p>Email: {invoice.customer_email}</p>}
          </div>

          {/* Storage Items */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-800 mb-4">Storage Charges:</h3>
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border p-2 text-left">Description</th>
                  <th className="border p-2 text-center">Qty</th>
                  <th className="border p-2 text-center">Total M3/Sqft</th>
                  <th className="border p-2 text-center">Duration</th>
                  <th className="border p-2 text-right">Rate per m³/sqft</th>
                  <th className="border p-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoice.storage_items?.map((item, index) => {
                  const isRate = item.unit_type === 'm3_rate' || item.unit_type === 'sqft_rate' || item.unit_type === 'pallet_rate';
                  return (
                  <tr key={`storage-${index}-${item.description || ''}`}>
                    <td className="border p-2">{item.description}</td>
                    <td className="border p-2 text-center">{item.quantity || '-'}</td>
                    <td className="border p-2 text-center">
                      {isRate ? `${item.total_units} ${item.unit_type === 'm3_rate' ? 'm³' : item.unit_type === 'pallet_rate' ? 'pallet(s)' : 'sqft'}` : '-'}
                    </td>
                    <td className="border p-2 text-center">
                      {isRate ? (parseInt(item.rate_days) === 0 ? 'COD' : `${item.rate_days} Days`) : item.rate_type === 'weekly' ? '7 Days' : '30 Days'}
                    </td>
                    <td className="border p-2 text-right">{formatCurrency(item.unit_rate)}</td>
                    <td className="border p-2 text-right">{formatCurrency(item.amount)}</td>
                  </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 font-semibold">
                  <td colSpan={5} className="border p-2 text-right">Storage Total:</td>
                  <td className="border p-2 text-right">{formatCurrency(invoice.storage_total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Handling Fees */}
          {invoice.handling_fees?.length > 0 && (
            <div className="mb-6">
              <h3 className="font-semibold text-gray-800 mb-4">Handling Fees:</h3>
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border p-2 text-left">Equipment</th>
                    <th className="border p-2 text-center">Usage Count</th>
                    <th className="border p-2 text-right">Rate/Usage</th>
                    <th className="border p-2 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.handling_fees.map((fee, index) => (
                    <tr key={`fee-${index}-${fee.description || fee.category || ''}`}>
                      <td className="border p-2">{fee.description || fee.category}</td>
                      <td className="border p-2 text-center">{fee.usage_count || '-'}</td>
                      <td className="border p-2 text-right">{fee.rate_per_use ? formatCurrency(fee.rate_per_use) : '-'}</td>
                      <td className="border p-2 text-right">{formatCurrency(fee.amount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 font-semibold">
                    <td colSpan={3} className="border p-2 text-right">Handling Total:</td>
                    <td className="border p-2 text-right">{formatCurrency(invoice.handling_total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Totals */}
          <div className="border-t-2 pt-4">
            <div className="flex justify-end">
              <div className="w-64 space-y-2">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>{formatCurrency(invoice.subtotal)}</span>
                </div>
                {invoice.gst_rate > 0 && (
                  <div className="flex justify-between">
                    <span>GST ({invoice.gst_rate}%):</span>
                    <span>{formatCurrency(invoice.gst_amount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-xl font-bold border-t pt-2">
                  <span>Amount Due:</span>
                  <span className="text-green-600">{formatCurrency(invoice.total_amount)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Info with QR */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h4 className="font-semibold text-blue-800 mb-3">Payment Information:</h4>
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-1">
                <p><strong>PayNow UEN:</strong> {companyInfo.paynow_uen}</p>
                <p className="text-sm text-gray-500 mt-1">Scan the QR code to pay via PayNow</p>
              </div>
              <img src="/qr_code.jpeg" alt="PayNow QR Code" className="w-32 h-32 object-contain border border-gray-200 rounded bg-white p-1" data-testid="paynow-qr" />
            </div>
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div className="mt-6">
              <h4 className="font-semibold text-gray-800 mb-2">Notes:</h4>
              <p className="text-gray-600 whitespace-pre-line">{invoice.notes}</p>
            </div>
          )}

          {/* Footer */}
          <div className="mt-8 pt-6 border-t text-center text-gray-500 text-sm">
            <p>Thank you for your business!</p>
            <p>{companyInfo.email} | Tel: {companyInfo.phone}</p>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          .print\\:hidden { display: none !important; }
        }
      `}</style>

      {/* Record Customer Payment */}
      <RecordPaymentDialog
        open={showPaymentModal}
        onOpenChange={setShowPaymentModal}
        invoice={invoice}
        invoiceSource="warehouse"
        onCreated={() => fetchInvoice && fetchInvoice()}
      />
    </div>
  );
};

export default WarehouseInvoiceDetail;
